import { diaDe, diasEntre, inicioDeSemana, sumarDias } from "./fechas";
import type { Etapa, Negocio } from "./tipos";

export type TipoElementoAgenda =
  | "reunion"
  | "presentacion"
  | "siguiente_paso"
  | "vuelta";

export type ElementoAgenda = {
  id: string;
  tipo: TipoElementoAgenda;
  /** ISO con hora (consultoría, presentación) o "YYYY-MM-DD" (siguiente paso, vuelta). */
  fecha: string;
  conHora: boolean;
  /** El proyecto con los datos de su negocio (proyectos_vista). */
  negocio: Negocio;
  titulo: string;
};

/** 33: el siguiente paso con hora va a su hora («YYYY-MM-DDTHH:MM», hora local). */
export const momentoPaso = (fecha: string, hora?: string | null) =>
  hora ? `${fecha}T${hora.slice(0, 5)}` : fecha;

/** Sin siguiente paso en la agenda: descartados y bajas. */
const llevaPaso = (e: Etapa) =>
  e !== "no_interesado" && e !== "baja" && e !== "cobrado";

/**
 * Elementos de agenda (SPEC.md §5) por proyecto: consultorías agendadas, presentaciones
 * (videollamada), siguientes pasos y vueltas de Largo plazo.
 */
export const elementosAgenda = (proyectos: Negocio[]): ElementoAgenda[] => {
  const elementos: ElementoAgenda[] = [];
  for (const n of proyectos) {
    const clave = String(n.proyecto_id ?? n.id);
    if (n.etapa === "consultoria_agendada" && n.fecha_reunion) {
      elementos.push({
        id: `reunion-${clave}`,
        tipo: "reunion",
        fecha: n.fecha_reunion,
        conHora: true,
        negocio: n,
        titulo: n.nombre,
      });
    }
    if (n.etapa === "presentacion_agendada" && n.fecha_presentacion) {
      elementos.push({
        id: `presentacion-${clave}`,
        tipo: "presentacion",
        fecha: n.fecha_presentacion,
        conHora: true,
        negocio: n,
        titulo: n.nombre,
      });
    }
    if (n.fecha_siguiente_paso && llevaPaso(n.etapa)) {
      elementos.push({
        id: `paso-${clave}`,
        tipo: "siguiente_paso",
        fecha: momentoPaso(n.fecha_siguiente_paso, n.hora_siguiente_paso),
        conHora: !!n.hora_siguiente_paso,
        negocio: n,
        titulo: n.siguiente_paso?.trim() || n.nombre,
      });
    }
    if (n.etapa === "largo_plazo" && n.fecha_vuelta) {
      elementos.push({
        id: `vuelta-${clave}`,
        tipo: "vuelta",
        fecha: n.fecha_vuelta,
        conHora: false,
        negocio: n,
        titulo: n.nombre,
      });
    }
  }
  return elementos.sort((a, b) => a.fecha.localeCompare(b.fecha));
};

export type GrupoAgenda = "vencidos" | "hoy" | "manana" | "semana" | "despues";

export const GRUPOS_AGENDA: GrupoAgenda[] = [
  "vencidos",
  "hoy",
  "manana",
  "semana",
  "despues",
];

/** Agrupa por día relativo a `hoy`: vencidos, hoy, mañana, resto de esta semana, después. */
export const agruparAgenda = (
  elementos: ElementoAgenda[],
  hoy: Date = new Date(),
): Record<GrupoAgenda, ElementoAgenda[]> => {
  const grupos: Record<GrupoAgenda, ElementoAgenda[]> = {
    vencidos: [],
    hoy: [],
    manana: [],
    semana: [],
    despues: [],
  };
  const finSemana = sumarDias(inicioDeSemana(hoy), 6);
  for (const e of elementos) {
    const dias = diasEntre(hoy, diaDe(e.fecha));
    if (dias < 0) grupos.vencidos.push(e);
    else if (dias === 0) grupos.hoy.push(e);
    else if (dias === 1) grupos.manana.push(e);
    else if (diaDe(e.fecha) <= finSemana) grupos.semana.push(e);
    else grupos.despues.push(e);
  }
  return grupos;
};

/** Elementos de un día concreto (vista calendario). */
export const elementosDelDia = (elementos: ElementoAgenda[], dia: Date) =>
  elementos.filter((e) => diasEntre(diaDe(e.fecha), dia) === 0);

/** Lo siguiente que toca con una ficha, para la tarjeta del tablero (02/10/2026). */
export type ProximaAccion = {
  tipo: TipoElementoAgenda;
  /** Texto del siguiente paso (vacío en reunión y vuelta: lo pone la pantalla). */
  texto: string;
  fecha: string;
  conHora: boolean;
  /** La fecha ya ha pasado (día anterior a hoy). */
  vencida: boolean;
};

/**
 * Consultoría o presentación si está agendada; si no, el siguiente paso; en Largo plazo,
 * la vuelta. Mismas reglas que la agenda (elementosAgenda).
 */
export const proximaAccion = (
  n: Pick<
    Negocio,
    | "etapa"
    | "fecha_reunion"
    | "fecha_presentacion"
    | "siguiente_paso"
    | "fecha_siguiente_paso"
    | "hora_siguiente_paso"
    | "fecha_vuelta"
  >,
  hoy: Date = new Date(),
): ProximaAccion | null => {
  const vencida = (fecha: string) => diasEntre(hoy, diaDe(fecha)) < 0;
  if (n.etapa === "consultoria_agendada" && n.fecha_reunion) {
    return {
      tipo: "reunion",
      texto: "",
      fecha: n.fecha_reunion,
      conHora: true,
      vencida: vencida(n.fecha_reunion),
    };
  }
  if (n.etapa === "presentacion_agendada" && n.fecha_presentacion) {
    return {
      tipo: "presentacion",
      texto: "",
      fecha: n.fecha_presentacion,
      conHora: true,
      vencida: vencida(n.fecha_presentacion),
    };
  }
  if (n.fecha_siguiente_paso && llevaPaso(n.etapa)) {
    return {
      tipo: "siguiente_paso",
      texto: n.siguiente_paso?.trim() ?? "",
      fecha: momentoPaso(n.fecha_siguiente_paso, n.hora_siguiente_paso),
      conHora: !!n.hora_siguiente_paso,
      vencida: vencida(n.fecha_siguiente_paso),
    };
  }
  if (n.etapa === "largo_plazo" && n.fecha_vuelta) {
    return {
      tipo: "vuelta",
      texto: "",
      fecha: n.fecha_vuelta,
      conHora: false,
      vencida: vencida(n.fecha_vuelta),
    };
  }
  return null;
};
